import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../config/api';
import CurrencyFormatter from '../../utils/CurrencyFormatter';

/**
 * 🎯 UserDetailsModal - Modal de Detalhes do Usuário
 * 
 * Exibe informações completas sobre um usuário (produtor, cliente ou afiliado)
 */
const UserDetailsModal = ({
  show,
  onHide,
  userId,
  userType // 'customer', 'producer', 'affiliate'
}) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show && userId) {
      loadUserDetails();
    }
  }, [show, userId]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('auth_token');
      
      // Usar endpoint de usuários para todos (clientes também são usuários)
      const endpoint = `${API_URL}/api/admin/users/${userId}`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success === false) {
        throw new Error(data.message || 'Erro ao carregar detalhes do usuário');
      }
      setUserData(data.data || data);
    } catch (err) {
      console.error('❌ Erro ao carregar usuário:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = () => {
    switch (userType) {
      case 'customer': return 'Cliente';
      case 'producer': return 'Produtor';
      case 'affiliate': return 'Afiliado';
      default: return 'Usuário';
    }
  };

  const getTypeIcon = () => {
    switch (userType) {
      case 'customer': return 'solar:user-bold';
      case 'producer': return 'solar:shop-bold';
      case 'affiliate': return 'solar:users-group-rounded-bold';
      default: return 'solar:user-bold';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Icon icon={getTypeIcon()} />
          Detalhes do {getTypeLabel()}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" />
            <p className="mt-2">Carregando detalhes...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            <Icon icon="solar:danger-bold" className="me-2" />
            {error}
          </div>
        ) : userData ? (
          <div className="row g-3">
            {/* Informações Básicas */}
            <div className="col-12">
              <h6 className="mb-3">
                <Icon icon="solar:information-circle-bold" className="me-2" />
                Informações Básicas
              </h6>
              <div className="card bg-light p-3">
                <div className="row g-2">
                  <div className="col-md-6">
                    <small className="text-muted d-block">Nome</small>
                    <strong>{userData.name || userData.customer_name || '-'}</strong>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted d-block">Email</small>
                    <strong>{userData.email || userData.customer_email || '-'}</strong>
                  </div>
                  {userData.phone && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">Telefone</small>
                      <strong>{userData.phone}</strong>
                    </div>
                  )}
                  {userData.document && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">CPF/CNPJ</small>
                      <strong>{userData.document}</strong>
                    </div>
                  )}
                  {userData.role && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">Tipo</small>
                      <strong>{userData.role}</strong>
                    </div>
                  )}
                  {userData.created_at && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">Cadastrado em</small>
                      <strong>{formatDate(userData.created_at)}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Estatísticas (se disponível) */}
            {(userData.total_orders || userData.total_spent) && (
              <div className="col-12">
                <h6 className="mb-3">
                  <Icon icon="solar:chart-line-bold" className="me-2" />
                  Estatísticas
                </h6>
                <div className="card bg-light p-3">
                  <div className="row g-2">
                    {userData.total_orders !== undefined && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">Total de Pedidos</small>
                        <strong className="text-primary">{userData.total_orders || 0}</strong>
                      </div>
                    )}
                    {userData.total_spent !== undefined && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">Total Gasto</small>
                        <strong className="text-success">
                          {CurrencyFormatter.centsToFormatted(userData.total_spent || 0)}
                        </strong>
                      </div>
                    )}
                    {userData.last_purchase && (
                      <div className="col-md-6">
                        <small className="text-muted d-block">Última Compra</small>
                        <strong>{formatDate(userData.last_purchase)}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal.Body>
      
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>
          <Icon icon="solar:close-bold" className="me-2" />
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserDetailsModal;

